import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Megaphone } from "lucide-react";
import { fetchAnnouncements, dismissAnnouncement, type AnnouncementItem } from "../api/announcements";
import { useAuth } from "../hooks/useAuth";
import { Button } from "./Button";
import { Markdown } from "./Markdown";

const ANNOUNCEMENTS_KEY = ["announcements"];

// The modal itself, split from the data wiring so the admin page can render a
// faithful preview of an announcement (existing or still being drafted) with
// its own button handlers. This IS what users see - style changes here change
// both the live overlay and admin previews.
export function AnnouncementModal({
  item,
  remaining,
  busy,
  onDismiss,
  onCta,
}: {
  item: Pick<AnnouncementItem, "title" | "body" | "imageUrl" | "ctaLabel" | "ctaUrl" | "createdAt">;
  /** Count shown as "N more unread"; pass 0 to hide. */
  remaining: number;
  busy?: boolean;
  onDismiss: () => void;
  /** Called with the CTA url when the CTA button is clicked. */
  onCta: (url: string) => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--scrim)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: 20,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-title"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-xl)",
          padding: 28,
          width: "100%",
          maxWidth: 440,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Megaphone size={16} style={{ color: "var(--text-accent)" }} />
          <p className="eyebrow" style={{ fontSize: 11, margin: 0 }}>Announcement</p>
        </div>
        <h2
          id="announcement-title"
          style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: "0 0 4px" }}
        >
          {item.title}
        </h2>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 14px" }}>
          {new Date(item.createdAt).toLocaleDateString(undefined, {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: "var(--text-secondary)",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt=""
              style={{
                width: "100%",
                maxHeight: 220,
                objectFit: "cover",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                marginBottom: 12,
                display: "block",
              }}
            />
          )}
          <Markdown text={item.body} />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginTop: 20,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {remaining > 0 ? `${remaining} more unread` : ""}
          </span>
          <div style={{ display: "flex", gap: 10 }}>
            {item.ctaLabel && item.ctaUrl ? (
              <>
                <Button variant="ghost" disabled={busy} onClick={onDismiss}>
                  Dismiss
                </Button>
                <Button variant="primary" disabled={busy} onClick={() => onCta(item.ctaUrl!)}>
                  {item.ctaLabel}
                </Button>
              </>
            ) : (
              <Button variant="primary" disabled={busy} onClick={onDismiss}>
                Got it
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Session-start announcement modal, mounted once in AppShell. Fetches the
// signed-in user's announcements when the app loads (staleTime Infinity — once
// per SPA session, so it fires on whichever page the user lands on) and shows
// undismissed ones oldest-first, one at a time. Dismissal is persisted
// server-side, so a dismissed announcement never comes back; dismissed items
// remain readable in the profile page's "Announcements" panel.
export function AnnouncementOverlay() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ANNOUNCEMENTS_KEY,
    queryFn: fetchAnnouncements,
    enabled: !!user,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => dismissAnnouncement(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<{ active: AnnouncementItem[]; dismissed: AnnouncementItem[] }>(
        ANNOUNCEMENTS_KEY,
        (prev) => {
          if (!prev) return prev;
          const item = prev.active.find((a) => a.id === id);
          return {
            active: prev.active.filter((a) => a.id !== id),
            dismissed: item
              ? [{ ...item, dismissedAt: new Date().toISOString() }, ...prev.dismissed]
              : prev.dismissed,
          };
        },
      );
    },
  });

  const active = query.data?.active ?? [];
  // API returns newest first; surface the oldest unread one first.
  const current = active[active.length - 1];
  if (!current) return null;

  return (
    <AnnouncementModal
      item={current}
      remaining={active.length - 1}
      busy={dismissMutation.isPending}
      onDismiss={() => dismissMutation.mutate(current.id)}
      onCta={(url) => {
        // Following the CTA counts as having read it - dismiss so the modal
        // doesn't come back, then go where it points.
        dismissMutation.mutate(current.id);
        if (url.startsWith("/")) {
          void navigate({ to: url });
        } else {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      }}
    />
  );
}
